import { EventEmitter, Injectable } from '@angular/core';
import { ToggleChatContainerE } from '@enums/attendances.enum';
import { ChatListTabsEnum } from '@enums/chat-list-tabs.enum';
import { ChatStatusE } from '@enums/chat-status.enum';
import { ChatTabsEnum } from '@enums/chat-tabs.enum';
import { MessageStatusE, MessageTypeE } from '@enums/message.enum';
import { SenderTypeE } from '@enums/sender.enum';
import { MessagesHelperService } from '@helpers/messages-helper.service';
import { linkToGlobalState } from '@helpers/redux-devtools.helper';
import { UserHelperService } from '@helpers/user-helper.service';
import { ChannelT } from '@interfaces/channel.interface';
import { ChatGroupI, ChatI, ChatMentionI, ListI } from '@interfaces/chat.interface';
import { ContactI, LoyaltyI } from '@interfaces/contact.interface';
import { MessageI, MessageStatusT } from '@interfaces/message.interface';
import { PageI } from '@interfaces/page.interface';
import { Session360DialogI } from '@interfaces/session-360dialog.interface';
import { UnsentMessageI } from '@interfaces/unsent-messages.interface';
import { ChatService } from '@modules/chat/chat.service';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { cloneArray, cloneObject } from '@node_modules/jssip/lib/Utils';
import { ChatStatusT, ChatT, ToggleChatContainerT } from '@omnitypes/chat.type';
import { MessageTypeT } from '@omnitypes/message.type';
import { SenderT } from '@omnitypes/sender.type';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatestAll,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { TimelineStoreService } from './timeline-store.service';
import { MessageErrorI } from '@interfaces/message-error.interface';
import { ChatReadStatus } from '@enums/chat-read-status.enum';
import { normalizeTextFromDifferentFormatation } from '@helpers/search-helper';
import { ChatFilterI } from '@interfaces/chat-filter.interface';
import { FilterChatSearchE } from '@enums/filter-chat-search.enum';
import {
  chatNotificationI,
  ChatsState,
  ChatStateFileMessageI,
  InfoRefreshListChatI,
  NotificationChatsI,
  NotificationChatsMentionI,
} from '@store/chat/interface/chat-store.interface';
import { ChatHelperService } from '@helpers/chat-helper.service';
import { PropertyChatE } from '@enums/chat-store.enum';
import { UserI } from '@interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatStoreService extends ComponentStore<ChatsState> {
  public searchFilterChanged: EventEmitter<string> = new EventEmitter();
  public chatTabChanged: EventEmitter<ChatTabsEnum> = new EventEmitter();
  public removeChatStyle: EventEmitter<boolean> = new EventEmitter();
  private _searchFilter: string = '';
  private _searchText: string = '';
  private unsentMessages: BehaviorSubject<UnsentMessageI[]> = new BehaviorSubject([] as UnsentMessageI[]);
  private contactPhotos: BehaviorSubject<{ [id: string]: string }> = new BehaviorSubject<{ [id: string]: string }>({});
  private latestMessages: BehaviorSubject<MessageI[]> = new BehaviorSubject([] as MessageI[]);
  private readChatStatus: BehaviorSubject<{ [id: string]: ChatReadStatus }> = new BehaviorSubject<{
    [id: string]: ChatReadStatus;
  }>({});

  public selectedTab?: ChatListTabsEnum;
  public selectedInternal: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  public otherTeams?: string[];
  private refreshChatSubject = new BehaviorSubject<InfoRefreshListChatI | null>(null);
  public refreshChat$ = this.refreshChatSubject.asObservable();
  private updateChatContactSubject = new BehaviorSubject<ChatI | null>(null);
  public updateChatContact$ = this.updateChatContactSubject.asObservable();

  private newInternalChatSubject = new Subject<ChatI>();
  public newInternalChat$ = this.newInternalChatSubject.asObservable();

  public notificationInternalTotal$: Observable<number> = this.getNotificationTotalsInternal();

  public chatListTabChanged: EventEmitter<ChatListTabsEnum> = new EventEmitter();
  public get searchFilter(): string {
    return this._searchFilter;
  }

  public set searchFilter(term: string) {
    if (this._searchFilter.toLocaleLowerCase() === term.toLocaleLowerCase()) return;
    term = normalizeTextFromDifferentFormatation(term);
    this._searchFilter = term;
    this.searchFilterChanged.emit(term);
  }

  public get searchText(): string {
    return this._searchText;
  }

  public set searchText(text: string) {
    this._searchText = text;
  }

  constructor(
    private readonly userHelper: UserHelperService,
    private readonly messageHelper: MessagesHelperService,
    private readonly chatService: ChatService,
    private readonly timelineStoreService: TimelineStoreService,
    private readonly store: Store,
    private readonly chatHelper: ChatHelperService
  ) {
    super(ChatStoreService.initialState());
    linkToGlobalState(this.state$, 'ChatStore', this.store);
    UserHelperService.logout$.subscribe((logouted: boolean) => {
      if (logouted) {
        this.clearAll();
      }
    });
  }
  public static initialState(initialChatType: ChatT = 'attendances'): ChatsState {
    return {
      chatGroups: [],
      chats: [],
      notificationChats: this.initStateNotificationChats(),
      notificationChatsMention: { chats: [], totals: 0 },
      currentChatId: '',
      currentChatDirectOpen: null,
      queue: [],
      in_queue: [],
      internal: [],
      finished: [],
      archived: [],
      api: [],
      delayed: [],
      waiting: [],
      flow: [],
      workflow: [],
      ai_assisted: [],
      new: [],
      transfer: [],
      kanban: [],
      forwardingMessages: {},
      filesMessage: [],
      chatSelected: null,
      chatType: initialChatType ?? 'attendances',
      chatToggle: ToggleChatContainerE.DEFAULT,
      contactAttendance: null,
      sortChat: -1,
      chatsFilter: {
        api: [],
        delayed: [],
        waiting: [],
        attendances: [],
        in_attendance: [],
        finished: [],
        archived: [],
        queue: [],
        in_queue: [],
        all: [],
        automatic: [],
        favorite: [],
        internal: [],
        start_flow: [],
        new: [],
        workflow: [],
        ai_assisted: [],
      },
      types: [],
      loading: false,
      activeTab: ChatTabsEnum.Details,
      chatFilterValues: ChatStoreService.createDefaultFilter(),
      moreFiltersEnabled: false,
    };
  }

  private static initStateNotificationChats(): NotificationChatsI {
    return {
      chats: [],
      queue: [],
      in_queue: [],
      internal: [],
      finished: [],
      archived: [],
      api: [],
      delayed: [],
      waiting: [],
      flow: [],
      workflow: [],
      ai_assisted: [],
      new: [],
      transfer: [],
      kanban: [],
      totals: 0,
      totalBySelectedChatType: 0,
    };
  }

  public notificationChatsTotals$: Observable<NotificationChatsI> = this.select(
    (state) => state.notificationChats
  ).pipe(debounceTime(500));

  public totalNotificationsSelectedType$: Observable<number> = this.select(
    (state) => state.notificationChats.totalBySelectedChatType
  );

  public notificationChatsMentionTotals$: Observable<NotificationChatsMentionI> = this.select(
    (state) => state.notificationChatsMention
  ).pipe(debounceTime(500));

  public chatType$ = this.select((state) => state.chatType);

  public chats$: Observable<ChatI[]> = this.select((state) => state.chats);
  public queue$: Observable<ChatI[]> = this.select((state) => state.queue);
  public inQueue$: Observable<ChatI[]> = this.select((state) => state.in_queue);
  public internal$: Observable<ChatI[]> = this.select((state) => state.internal);
  public finished$: Observable<ChatI[]> = this.select((state) => state.finished);
  public archived$: Observable<ChatI[]> = this.select((state) => state.archived);
  public flow$: Observable<ChatI[]> = this.select((state) => state.flow);
  public workflow$: Observable<ChatI[]> = this.select((state) => state.workflow);
  public aiAssisted$: Observable<ChatI[]> = this.select((state) => state.ai_assisted);
  public new$: Observable<ChatI[]> = this.select((state) => state.new);
  public transfer$: Observable<ChatI[]> = this.select((state) => state.transfer);
  public kanban$: Observable<ChatI[]> = this.select((state) => state.kanban);
  public delayed$: Observable<ChatI[]> = this.select((state) => state.delayed);
  public waiting$: Observable<ChatI[]> = this.select((state) => state.waiting);
  public api$: Observable<ChatI[]> = this.select((state) => state.api);

  public currentChatId$: Observable<string> = this.select((state) => state.currentChatId);
  public readonly loading$ = this.select((state) => state.loading);

  // keyed by chatId so an open reply stays scoped to its conversation and returns when the chat is reselected
  public forwardingMessage$: Observable<MessageI | null> = this.select(
    (state) => state.forwardingMessages[state.currentChatId] ?? null
  );
  public filesMessage$: Observable<ChatStateFileMessageI[]> = this.select((state) => state.filesMessage);

  public chatSelected$: Observable<boolean | null> = this.select((state) => state.chatSelected);
  public readonly queueQuantity$: Observable<number> = this.select((state) => state.queue.length);
  public chatToggle$: Observable<ToggleChatContainerT> = this.select((state) => state.chatToggle).pipe(
    distinctUntilChanged()
  );
  public contactAttendance$: Observable<ContactI | null> = this.select((state) => state.contactAttendance);
  public sortChat$: Observable<number> = this.select((state) => state.sortChat);
  public chatFilterValues$: Observable<ChatFilterI> = this.select((state) => state.chatFilterValues);
  public moreFiltersEnabled$: Observable<boolean> = this.select((state) => state.moreFiltersEnabled);

  public hideButtonContainer: boolean = false;
  public selectedMessages: string[] = [];
  public completeSelectedMessages: MessageI[] = [];
  public selectedContacts: ListI[] = [];
  public selectedChatId: string = '';

  public currentChat$: Observable<ChatI | undefined> = this.select((state) => {
    if (!state.currentChatId && !state.currentChatDirectOpen) return undefined;
    return (
      state.currentChatDirectOpen ||
      state.chats.find((chat) => chat._id === state.currentChatId) ||
      state.queue.find((chat) => chat._id === state.currentChatId) ||
      state.in_queue.find((chat) => chat._id === state.currentChatId) ||
      state.internal.find((chat) => chat._id === state.currentChatId) ||
      state.finished.find((chat) => chat._id === state.currentChatId) ||
      state.archived.find((chat) => chat._id === state.currentChatId) ||
      state.flow.find((chat) => chat._id === state.currentChatId) ||
      state.workflow.find((chat) => chat._id === state.currentChatId) ||
      state.ai_assisted.find((chat) => chat._id === state.currentChatId) ||
      state.new.find((chat) => chat._id === state.currentChatId) ||
      state.delayed.find((chat) => chat._id === state.currentChatId) ||
      state.waiting.find((chat) => chat._id === state.currentChatId) ||
      state.api.find((chat) => chat._id === state.currentChatId) ||
      state.kanban.find((chat) => chat._id === state.currentChatId)
    );
  }).pipe(distinctUntilChanged((prev, curr) => prev?._id === curr?._id));

  public currentChatGroup$: Observable<ChatGroupI | undefined> = this.select((state) => {
    const { currentChatId, chatGroups } = state;
    if (!currentChatId) return undefined;
    for (const chatGroup of chatGroups) {
      const chat = chatGroup.chats.find((chat) => chat._id === currentChatId);
      if (chat) return chatGroup;
    }

    return undefined;
  });

  public chatsFilter$: Observable<{ [type in ChatT]: ChatI[] }> = this.select((state) => state.chatsFilter);
  public activeTab$ = this.select((state) => state.activeTab).pipe(distinctUntilChanged());

  public setActiveTab(tab: ChatTabsEnum): void {
    this.patchState({ activeTab: tab });
  }

  public getCurrentActiveTab() {
    return this.get().activeTab;
  }

  public toogleSortChat(): void {
    this.setState((state) => ({ ...state, sortChat: state.sortChat * -1 }));
  }

  public changeChatSelected(chatId: string = ''): void {
    this.hideButtonContainer = false;
    this.setState((state) => ({ ...state, chatSelected: !state.chatSelected }));
    this.selectedChatId = chatId;
  }

  public changeSelectdeMessages(newMessages: string[], newCompleteMessages: MessageI[]): void {
    this.selectedMessages = newMessages;
    this.completeSelectedMessages = newCompleteMessages;
  }

  public updateSelectedContacts(contacts: ListI[]): void {
    this.selectedContacts = contacts;
  }

  public setNullChatSelected(): void {
    this.hideButtonContainer = false;
    this.setState((state) => ({ ...state, chatSelected: null }));
  }

  public isChatMessageType(chatId: string, type: MessageTypeT) {
    return this.select((state) => {
      const chat = state.chats.find((chat) => chat._id === chatId && chat.types?.includes(type));
      if (chat) return !!chat;
      const chatInternal = state.internal.find((chat) => chat._id === chatId && chat.types?.includes(type));
      if (chatInternal) return !!chatInternal;
      return false;
    });
  }

  public addMessageInChatById(chatId: string, message: MessageI, status: ChatStatusT = 'attendance'): void {
    try {
      switch (status) {
        case 'internal':
          this.addMessageInInternalChat(chatId, message);
          break;
        default:
          this.addMessageInChat(chatId, message);
          break;
      }
    } catch (error) {
      throw new Error(`ChatStoreService addMessageInChatById error: ${error}`);
    }
  }

  public updateMessageStatus(
    chatId: string,
    messageId: string,
    status: MessageStatusT,
    chatStatus: ChatStatusT = 'attendance',
    newMessageId?: string
  ): void {
    try {
      switch (chatStatus) {
        case 'internal':
          this.updateInternalChatMessageStatus(chatId, messageId, status, newMessageId);
          break;

        default:
          this.updateChatMessageStatus(chatId, messageId, status, newMessageId);
          break;
      }
    } catch (error) {
      throw new Error(`ChatStoreService updateMessageStatus error: ${error}`);
    }
  }

  public filterChatByContactInfo(
    contactInfo: string,
    tmpChat: Observable<ChatI[]>,
    type: ChatT | string = 'attendances'
  ): Observable<ChatI[]> {
    if (contactInfo === '') return tmpChat;
    const regex = new RegExp(`^${contactInfo}`, 'i');
    switch (type) {
      case ChatStatusE.QUEUE:
        return this.filterQueueByContactInfo(contactInfo, regex);
      case 'attendances':
        return this.filterChatsByContactInfo(contactInfo, regex);
      case ChatStatusE.INTERNAL:
        return this.filterInternalByContactInfo(contactInfo, regex);
      case ChatStatusE.FINISHED:
        return this.filterFinishedByContactInfo(contactInfo, regex);
      case ChatStatusE.ARCHIVED:
        return this.filterArchivedByContactInfo(contactInfo, regex);
      case ChatStatusE.STARTFLOW:
        return this.filterFlowByContactInfo(contactInfo, regex);
      case ChatStatusE.NEW:
        return this.filterNewByContactInfo(contactInfo, regex);
      case ChatStatusE.TRANSFER:
        return this.filterTransferByContactInfo(contactInfo, regex);
      case 'kanban':
        return this.filterKanbanByContactInfo(contactInfo, regex);
      case ChatStatusE.API:
        return this.filterApiByContactInfo(contactInfo, regex);
      case ChatStatusE.DELAYED:
        return this.filterDelayedByContactInfo(contactInfo, regex);
      case ChatStatusE.WAITING:
        return this.filterWaitingByContactInfo(contactInfo, regex);
      case ChatStatusE.WORKFLOW:
        return this.filterWorkflowByContactInfo(contactInfo, regex);
      case 'in_queue':
        return this.filterInQueueByContactInfo(contactInfo, regex);
      default:
        return tmpChat;
    }
  }

  public getChatByContactId(contactId: string): Observable<ChatI | undefined> {
    return this.select((state) => {
      const chat = state.chats.find((chat) => chat.contact?._id === contactId);
      if (chat) return chat;
      return undefined;
    });
  }

  public getChatById(chatId: string): Observable<ChatI | undefined> {
    return this.select((state) => {
      const chat = state.chats.find((chat) => chat._id === chatId);
      if (chat) return chat;
      const chatInternal = state.internal.find((chat) => chat._id === chatId);
      if (chatInternal) return chatInternal;
      return undefined;
    });
  }

  public getContactByChatId(chatId: string): Observable<ContactI | undefined> {
    return this.select((state) => state.chats.find((chat) => chat._id === chatId)?.contact);
  }

  private readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public setCurrentChat = this.effect<string>((chatId$) =>
    chatId$.pipe(
      tap(() => this.setLoading(true)),
      tap(() => this.unsetCurrentChat()),
      switchMap((chatId) => {
        if (!chatId) {
          this.setLoading(false);
          return EMPTY;
        }

        this.setCurrentChatId(chatId);
        this.setSelectedChatId(chatId);
        this.changeChatSelected(chatId);
        this.changeSelectdeMessages([], []);

        return of(chatId);
      }),
      tap(() => this.setLoading(false))
    )
  );

  public async unsetCurrentChat() {
    this.setCurrentChatId('');
    this.setNullChatSelected();
    this.setSelectedChatId('');
    this.setChatToggle(ToggleChatContainerE.DEFAULT);
    this.timelineStoreService.setContact({} as ContactI);
  }

  public async unsetCurrentChatIfCurrent(chatId: string) {
    const state = await firstValueFrom(this.select((state) => state));
    const currentChatId = state?.currentChatId;
    if (currentChatId && currentChatId === chatId) {
      this.unsetCurrentChat();
    }
  }

  public setCurrentChatId(chatId: string): void {
    this.setState((state) => ({
      ...state,
      currentChatDirectOpen: null,
      currentChatId: chatId,
      notificationChats: this.removeNotification(cloneObject(state), chatId),
      notificationChatsMention: this.removeNotificationMention(cloneObject(state.notificationChatsMention), chatId),
    }));
  }

  public async setCurrentChatDirectOpen(chat: ChatI) {
    this.setState((state) => ({
      ...state,
      currentChatDirectOpen: chat,
      currentChatId: chat._id!,
      notificationChats: this.removeNotification(cloneObject(state), chat._id!),
      notificationChatsMention: this.removeNotificationMention(cloneObject(state.notificationChatsMention), chat._id!),
    }));
  }

  public populateGroupStore(chatGroups: ChatGroupI[]): void {
    this.setState((state) => ({ ...state, chatGroups: chatGroups }));
  }

  public populateStore(chats: ChatI[], overwrite: boolean = true, first: boolean = false, kanban = false): void {
    if (!chats || chats?.length === 0) return;

    chats.forEach((chat: any) => {
      if (!chat) return;
      chat = this.updateSupervisorReadStatus(chat);
      const { latestMessage, readStatus, ...chatWithLatestRemoved } = chat;
      this.updateLatestMessage(latestMessage);
      this.updateReadStatus(chat._id, readStatus);
      this.clearBeforePopulate(chatWithLatestRemoved._id, overwrite);
      this.populateQueues(chatWithLatestRemoved, overwrite, first, kanban);
    });
    this.loadNotificationsByChats(chats);
  }

  private loadNotificationsByChats(chats: ChatI[]): void {
    const chatsNotification: chatNotificationI[] = chats
      .filter((chat) => {
        const readStatus = this.readChatStatus.getValue()?.[chat._id!];
        return readStatus === ChatReadStatus.UNREAD || (chat?.unreadMessagesCount || 0) > 0;
      })
      .map((chat) => ({
        _id: chat._id!,
        count: chat.unreadMessagesCount || 0,
        status: chat.status as ChatStatusE,
        inQueue: this.isInQueueChat(chat),
      }));

    if (chatsNotification?.length === 0) return;

    this.setNotification(chatsNotification);
  }

  public setMoreFiltersEnabled(enabled: boolean): void {
    this.setState((state) => ({ ...state, moreFiltersEnabled: enabled }));
  }

  public setChatFilterValues(chatFilterValues: Partial<ChatFilterI>, refresh: boolean = true): void {
    const chatFilterValuesSet = { ...this.getCurrentChatFilterValues(), ...chatFilterValues };
    this.setState((state) => ({ ...state, chatFilterValues: chatFilterValuesSet }));
    if (refresh) {
      this.refreshChat();
    }
  }

  public getCurrentChatFilterValues(): ChatFilterI {
    const state = this.get();
    return state.chatFilterValues ?? ChatStoreService.createDefaultFilter();
  }

  private static createDefaultFilter(): ChatFilterI {
    return {
      channel: '',
      tag: '',
      content: '',
      email: '',
      endDate: '',
      name: '',
      onlyNotRead: false,
      protocol: '',
      searchField: FilterChatSearchE.NAME,
      startDate: '',
      telephone: '',
      onlyMentions: false,
      team: '',
      agent: '',
      otherTeam: [] as string[],
    } as ChatFilterI;
  }

  private getAllQueues() {
    return [
      { stream: this.queue$, property: 'queue', status: ChatStatusE.QUEUE },
      { stream: this.inQueue$, property: 'in_queue', status: ChatStatusE.QUEUE },
      { stream: this.chats$, property: 'chats', status: ChatStatusE.ATTENDANCE },
      { stream: this.finished$, property: 'finished', status: ChatStatusE.FINISHED },
      { stream: this.archived$, property: 'archived', status: ChatStatusE.ARCHIVED },
      { stream: this.flow$, property: 'flow', status: ChatStatusE.STARTFLOW },
      { stream: this.workflow$, property: 'workflow', status: ChatStatusE.WORKFLOW },
      { stream: this.aiAssisted$, property: 'ai_assisted', status: ChatStatusE.AI_ASSISTED },
      { stream: this.new$, property: 'flow', status: ChatStatusE.NEW },
      { stream: this.transfer$, property: 'flow', status: ChatStatusE.TRANSFER },
      { stream: this.internal$, property: 'internal', status: ChatStatusE.INTERNAL },
      { stream: this.kanban$, property: 'kanban', status: 'kanban' },
      { stream: this.api$, property: 'api', status: ChatStatusE.API },
      { stream: this.delayed$, property: 'delayed', status: ChatStatusE.DELAYED },
      { stream: this.waiting$, property: 'waiting', status: ChatStatusE.WAITING },
    ];
  }

  private populateQueues(chat, overwrite: boolean = true, first: boolean = false, kanban = false) {
    this.getAllQueues().forEach(({ stream, property, status }) => {
      if (status === chat.status || (property == 'kanban' && (chat.kanbanCategory || kanban))) {
        this.setState((state) => {
          if (status === ChatStatusE.QUEUE) {
            const isInQueue = this.isInQueueChat(chat);
            if ((property === 'in_queue' && !isInQueue) || (property === 'queue' && isInQueue)) {
              return state;
            }
          }
          const queue = state[property];
          const exists = !!queue.find((chatQueue: ChatI) => chatQueue._id === chat._id);

          if (!overwrite && exists) {
            return state;
          }
          return { ...state, [property]: first ? [chat, ...queue] : [...queue, chat] };
        });
      }
    });
  }

  private updateSupervisorReadStatus(chat: ChatI) {
    const userId = this.userHelper.getUserAttributes()!['custom:userId'];
    const { supervisorReadStatus } = chat;

    if (chat.agent?._id === userId || chat.internalAgents.find((agent) => agent._id === userId)) {
      return chat;
    }

    const status = supervisorReadStatus?.find((status) => status._id === userId);

    if (status) {
      chat.readStatus = status?.readStatus ?? ChatReadStatus.UNREAD;
      chat.unreadMessagesCount = status?.unreadMessagesCount ?? chat.messages?.length;
    }

    return chat;
  }

  private isInQueueChat(chat: ChatI) {
    return this.chatHelper.isInQueueChat(chat);
  }

  private clearBeforePopulate(chatId, overwrite: boolean = true) {
    this.getAllQueues().forEach(({ stream, property, status }) => {
      this.setState((state) => {
        const queue = state[property];
        const exists = !!queue.find((chatQueue: ChatI) => chatQueue._id === chatId);

        if (!overwrite && exists) {
          return state;
        }

        return { ...state, [property]: [...state[property]].filter((chat) => chat._id !== chatId) };
      });
    });
  }

  public async updateWhatsappContactPhoto() {
    const allChats$ = of(
      this.chats$,
      this.finished$,
      this.internal$,
      this.queue$,
      this.archived$,
      this.flow$,
      this.workflow$,
      this.aiAssisted$,
      this.new$,
      this.api$,
      this.delayed$,
      this.waiting$,
      this.transfer$
    ).pipe(
      combineLatestAll(),
      map((combinedChats: ChatI[][]) => combinedChats.flat())
    );

    const chats = await firstValueFrom(allChats$);

    const contactPhotos = { ...this.contactPhotos.getValue() };
    for (const chat of chats) {
      if (chat.contact && chat.contact.avatar) {
        contactPhotos[chat.contact._id!] = chat.contact.avatar;
      }
    }
    this.contactPhotos.next(contactPhotos);
  }

  public addContactPhoto(contactId: string, data: string) {
    const contactPhotos = this.contactPhotos.getValue();
    if (contactPhotos[contactId] !== data) {
      this.contactPhotos.next({ ...contactPhotos, [contactId]: data });
    }
  }

  public updateReadStatus(chatId: string, data: ChatReadStatus) {
    const readChatStatus = this.readChatStatus.getValue();
    if (readChatStatus[chatId] !== data) {
      this.readChatStatus.next({ ...readChatStatus, [chatId]: data });
    }
  }

  public async removeChat(chatId: string) {
    this.setState((state) => {
      const notificationChats = this.removeNotification(cloneObject(state), chatId);
      return {
        ...state,
        notificationChats,
        chats: state.chats.filter((chat) => chat._id !== chatId),
        queue: state.queue.filter((chat) => chat._id !== chatId),
        internal: state.internal.filter((chat) => chat._id !== chatId),
        flow: state.flow.filter((chat) => chat._id !== chatId),
        workflow: state.workflow.filter((chat) => chat._id !== chatId),
        ai_assisted: state.ai_assisted.filter((chat) => chat._id !== chatId),
      };
    });
  }

  public updateCountStateByType(
    chatId: string,
    type?: ChatStatusT,
    allMessageRead: boolean = true,
    isNewMessage: boolean = false
  ) {
    const updateChat = (chats: ChatI[], state) => {
      return chats.map((chat) => {
        if (chat._id === chatId) {
          const notSelected = state && !state.chatSelected;
          const canUpdateCount = isNewMessage && (notSelected || state.currentChatId !== chatId || document.hidden);
          const unreadMessagesCount = canUpdateCount
            ? (chat.unreadMessagesCount || 0) + 1
            : allMessageRead
            ? 0
            : chat.unreadMessagesCount || 0;
          return {
            ...chat,
            unreadMessagesCount,
          };
        }
        return chat;
      });
    };

    const chatTypes = {
      queue: ChatStatusE.QUEUE,
      attendance: 'chats',
      internal: ChatStatusE.INTERNAL,
      start_flow: 'flow',
      api: ChatStatusE.API,
      delayed: ChatStatusE.DELAYED,
      waiting: ChatStatusE.WAITING,
      workflow: ChatStatusE.WORKFLOW,
      ai_assisted: ChatStatusE.AI_ASSISTED,
    };

    if (type && chatTypes[type]) {
      const chatType = chatTypes[type];
      this.setState((state) => ({
        ...state,
        [chatType]: updateChat(state[chatType], state),
      }));
    } else {
      for (const key in chatTypes) {
        const chatType = chatTypes[key];
        this.setState((state) => ({
          ...state,
          [chatType]: updateChat(state[chatType], state),
        }));
      }
    }
  }

  public async setNotificationsOnNewMessage(message: MessageI) {
    try {
      const chatId = message.chat as string;
      const chat = await this.existsChatInStore({ _id: chatId });
      const chatStatus = chat?.status;
      const inQueue = chat ? this.isInQueueChat(chat) : false;

      if (!chat) return;

      if (chatId && chatStatus) {
        this.addNotification(chatId, chatStatus as ChatStatusE, inQueue);
      }
    } catch (error) {
      console.error('Error setting notifications on new message:', error);
    }
  }

  private mapStatusToLists = {
    queue: ['queue', 'in_queue'],
    attendance: ['chats'],
    internal: ['internal'],
    finished: ['finished'],
    archived: ['archived'],
    start_flow: ['flow'],
    workflow: ['workflow'],
    ai_assisted: ['ai_assisted'],
    api: ['api'],
    delayed: ['delayed'],
    waiting: ['waiting'],
    new: ['flow'],
    transfer: ['flow'],
  };

  public async removeChatsByTypeReturning(chatsToRemove: ChatI[]): Promise<Array<ChatI>> {
    const mapLists = this.mapStatusToLists;
    const currentState = await firstValueFrom(this.select((state) => state));
    const result: Array<ChatI> = [];
    for (const chatToRemove of chatsToRemove) {
      const listNames = mapLists[chatToRemove.status];
      if (listNames) {
        for (const listName of listNames) {
          const list = currentState[listName];
          const found = list.find((chat) => chat._id === chatToRemove._id);
          if (found) {
            const hasOnResult = result.find((row) => row._id === found._id);
            if (!hasOnResult) {
              result.push(found);
            }
          }
        }
      }
    }
    this.setState((state) => {
      const newState = {
        ...state,
      };
      for (const chatToRemove of chatsToRemove) {
        const listNames = mapLists[chatToRemove.status];
        if (listNames) {
          for (const listName of listNames) {
            const list = newState[listName];
            newState[listName] = [...list.filter((chat) => chat._id !== chatToRemove._id)];
          }
        }
      }
      return newState;
    });
    return result;
  }

  public async restoreChatByStatus(chatsToInsert: ChatI[]) {
    const mapLists = this.mapStatusToLists;
    this.setState((state) => {
      const newState = {
        ...state,
      };
      for (const chatToInsert of chatsToInsert) {
        const listNames = mapLists[chatToInsert.status];
        if (listNames) {
          for (const listName of listNames) {
            const list = newState[listName];
            newState[listName] = [...list.filter((chat) => chat._id !== chatToInsert._id), chatToInsert];
          }
        }
      }
      return newState;
    });
  }

  public removeChatByType(chatId: string, type: ChatStatusT) {
    switch (type) {
      case 'queue':
        this.setState((state) => ({ ...state, queue: state.queue.filter((chat) => chat._id !== chatId) }));
        this.setState((state) => ({ ...state, in_queue: state.in_queue.filter((chat) => chat._id !== chatId) }));
        break;
      case 'attendance':
        this.setState((state) => ({ ...state, chats: state.chats.filter((chat) => chat._id !== chatId) }));
        break;
      case 'internal':
        this.setState((state) => ({ ...state, internal: state.internal.filter((chat) => chat._id !== chatId) }));
        break;
      case 'finished':
        this.setState((state) => ({ ...state, finished: state.finished.filter((chat) => chat._id !== chatId) }));
        break;
      case 'archived':
        this.setState((state) => ({ ...state, archived: state.archived.filter((chat) => chat._id !== chatId) }));
        break;
      case 'start_flow':
        this.setState((state) => ({ ...state, flow: state.flow.filter((chat) => chat._id !== chatId) }));
        break;
      case 'workflow':
        this.setState((state) => ({ ...state, workflow: state.workflow.filter((chat) => chat._id !== chatId) }));
        break;
      case 'ai_assisted':
        this.setState((state) => ({ ...state, ai_assisted: state.ai_assisted.filter((chat) => chat._id !== chatId) }));
        break;
      case 'api':
        this.setState((state) => ({ ...state, api: state.api.filter((chat) => chat._id !== chatId) }));
        break;
      case 'delayed':
        this.setState((state) => ({ ...state, delayed: state.delayed.filter((chat) => chat._id !== chatId) }));
        break;
      case ChatStatusE.WAITING:
        this.setState((state) => ({ ...state, waiting: state.waiting.filter((chat) => chat._id !== chatId) }));
        break;
      case 'new':
        this.setState((state) => ({ ...state, flow: state.flow.filter((chat) => chat._id !== chatId) }));
        break;
      case 'transfer':
        this.setState((state) => ({ ...state, flow: state.flow.filter((chat) => chat._id !== chatId) }));
        break;
      default:
        break;
    }
  }

  public clearChatByType(type: ChatStatusT) {
    switch (type) {
      case 'queue':
        this.setState((state) => ({ ...state, queue: [], in_queue: [] }));
        break;
      case 'attendance':
        this.setState((state) => ({ ...state, chats: [] }));
        break;
      case 'internal':
        this.setState((state) => ({ ...state, internal: [] }));
        break;
      case 'finished':
        this.setState((state) => ({ ...state, finished: [] }));
        break;
      case 'archived':
        this.setState((state) => ({ ...state, archived: [] }));
        break;
      case 'start_flow':
        this.setState((state) => ({ ...state, flow: [] }));
        break;
      case 'workflow':
        this.setState((state) => ({ ...state, workflow: [] }));
        break;
      case 'ai_assisted':
        this.setState((state) => ({ ...state, ai_assisted: [] }));
        break;
      case 'delayed':
        this.setState((state) => ({ ...state, delayed: [] }));
        break;
      case ChatStatusE.WAITING:
        this.setState((state) => ({ ...state, waiting: [] }));
        break;
      case 'new':
        this.setState((state) => ({ ...state, flow: [] }));
        break;
      case 'transfer':
        this.setState((state) => ({ ...state, flow: [] }));
        break;
      default:
        break;
    }
  }

  public updateMessageStatusAsReadByChatId(
    chatId: string,
    senderType: SenderT,
    forceUpdateAll = false,
    chatType?: ChatStatusT,
    isNewMessage?: boolean
  ): void {
    this.setState((state) => {
      const chats = cloneArray(state.chats);
      const internal = cloneArray(state.internal);

      this.updateCountStateByType(chatId, chatType, true, isNewMessage);

      return {
        ...state,
        chats: this.updateStatusMessage(chats, chatId, senderType, forceUpdateAll),
        internal: this.updateStatusMessage(internal, chatId, senderType, forceUpdateAll),
      };
    });
  }

  private updateStatusMessage(chats: ChatI[], chatId: string, senderType: SenderT, forceUpdateAll: boolean) {
    return chats.map((chat) => {
      if (chat._id === chatId && chat.messages) {
        return {
          ...chat,
          messages: chat.messages.map((message) => {
            if (message?.sender?.type === senderType && message.status !== MessageStatusE.READ) {
              if (forceUpdateAll) {
                return {
                  ...message,
                  status: MessageStatusE.READ,
                };
              }
              if (this.messageHelper.getSenderTypeByMessage(message) === SenderTypeE.CONTACT) {
                return {
                  ...message,
                  status: MessageStatusE.READ,
                };
              }
              return message;
            }
            return message;
          }),
        };
      }
      return chat;
    });
  }

  private updateStatusDeliveredMessage(chats: ChatI[], chatId: string, forceUpdateAll: boolean) {
    return chats.map((chat) => {
      if (chat._id === chatId && chat.messages) {
        return {
          ...chat,
          unreadMessagesCount: 0,
          messages: chat.messages.map((message) => {
            if (message.status === 'sent') {
              if (forceUpdateAll) {
                return {
                  ...message,
                  status: MessageStatusE.DELIVERED,
                };
              }
              if (this.messageHelper.getSenderTypeByMessage(message) === SenderTypeE.AGENT) {
                return {
                  ...message,
                  status: MessageStatusE.DELIVERED,
                };
              }
              return message;
            }
            return message;
          }),
        };
      }
      return chat;
    });
  }

  public updateMessageStatusAsDeliveredByChatId(chatId: string, senderType: SenderT, forceUpdateAll = false): void {
    this.setState((state) => {
      const chats = cloneArray(state.chats);
      const internal = cloneArray(state.internal);

      return {
        ...state,
        chats: this.updateStatusDeliveredMessage(chats, chatId, forceUpdateAll),
        internal: this.updateStatusDeliveredMessage(internal, chatId, forceUpdateAll),
      };
    });
  }

  public filterAllChatsByChannel(
    chatList: Observable<ChatI[]>,
    channelType: ChannelT,
    chatType: ChatT
  ): Observable<ChatI[]> {
    switch (chatType) {
      case 'queue':
        return this.filterQueueByChannel(channelType);
      case 'attendances':
        return this.filterChatsByChannel(channelType);
      default:
        return chatList;
    }
  }

  public getInternalChatByInternalAgent(internalAgentId: string): Observable<ChatI | void> {
    return this.internal$.pipe(
      map((internal) =>
        internal.find((chat) => {
          if (chat.internalAgents) {
            return chat.internalAgents.some((agent) => {
              return agent._id === internalAgentId;
            });
          }
          return false;
        })
      )
    );
  }

  public getFinishedChatByAgent(id: string): Observable<ChatI | void> {
    return this.finished$.pipe(map((finished) => finished.find((chat) => chat._id === id)));
  }

  public setChatType(type: ChatT): void {
    this.setState((state) => ({ ...state, chatType: type }));
  }

  public updateChatIdInInternal(oldId: string, newId: string): void {
    this.setState((state) => ({
      ...state,
      internal: state.internal.map((chat) => {
        if (chat._id === oldId) {
          return { ...chat, _id: newId };
        }
        return chat;
      }),
    }));
  }

  public updateMessageByChatStatus(
    chatStatus: ChatStatusT,
    chatId: string,
    messageId: string,
    status: MessageStatusT,
    errors?: MessageErrorI[]
  ): void {
    switch (chatStatus) {
      case 'internal':
        this.updateInternalChatMessageStatus(chatId, messageId, status);
        break;
      default:
        this.updateChatMessageStatus(chatId, messageId, status, undefined, errors);
        break;
    }
  }

  public setChatToggle(toggle: ToggleChatContainerT) {
    this.setState((state) => ({
      ...state,
      chatToggle: toggle,
    }));
  }

  public getUnsentMessageById(messageId: string): Observable<UnsentMessageI | undefined> {
    return this.unsentMessages.asObservable().pipe(
      map((unsentMessages: UnsentMessageI[]) => {
        return unsentMessages.find((message: UnsentMessageI) => message._id === messageId);
      })
    );
  }

  public getLatestMessageById(chatId: string): Observable<MessageI | undefined> {
    return this.latestMessages.asObservable().pipe(
      map((unsentMessages: MessageI[]) => {
        return unsentMessages.find((message: MessageI) => message.chat === chatId);
      })
    );
  }

  public getAllLatestMessages(): Observable<MessageI[]> {
    return this.latestMessages.asObservable();
  }

  public getContactPhotoById(contactId: string): Observable<string | undefined> {
    return this.contactPhotos.asObservable().pipe(map((contactPhotos) => contactPhotos[contactId]));
  }

  public getReadChatStatusById(chatId: string): Observable<ChatReadStatus | undefined> {
    return this.readChatStatus.asObservable().pipe(map((readChatStatus) => readChatStatus[chatId]));
  }

  private lastReconnectRefreshAt = 0;

  public refreshChat(byReconnection?: boolean) {
    if (byReconnection) {
      // A reconnect fires refreshChat(true) from two places (connect callback + reconnected$);
      // collapse the near-simultaneous double so the list/messages reconcile once (OMNI-7778).
      const now = Date.now();
      if (now - this.lastReconnectRefreshAt < 800) {
        return;
      }
      this.lastReconnectRefreshAt = now;
    }
    this.refreshChatSubject.next({
      filters: this.getCurrentChatFilterValues(),
      sort: this.get().sortChat,
      moreFiltersEnabled: this.get().moreFiltersEnabled,
      byReconnection,
    });
  }

  public clearUnsentMessageById(messageId: string): void {
    const currentMessages = this.unsentMessages.getValue();
    const updatedMessages = currentMessages.filter((message: UnsentMessageI) => message._id !== messageId);
    this.unsentMessages.next(updatedMessages);
  }

  public async updateUnsentMessage(_id: string, message: string) {
    const updatedMessage = {
      _id,
      message,
    };

    const unsentMessages = await firstValueFrom(this.unsentMessages);

    const latestUnsentMessage = unsentMessages.find((unsentMessages) => unsentMessages._id === _id);

    if (!latestUnsentMessage) {
      unsentMessages.push(updatedMessage);
    }

    const updatedUnsentMessages = unsentMessages.map((unsentMessage) => {
      if (_id === unsentMessage._id) {
        return updatedMessage;
      }

      return unsentMessage;
    });

    this.unsentMessages.next(updatedUnsentMessages);
  }

  public async updateSessionFromMessage(chatId: string, session: Session360DialogI) {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          return { ...chat, session360Dialog: session };
        }
        return chat;
      }),
    }));
  }

  public updateLatestMessage(message: MessageI) {
    if (!message) return;
    const latestMessages = this.latestMessages.getValue();

    if (!latestMessages.find((latestMessage) => latestMessage?.chat === message?.chat)) {
      latestMessages.push(message);
    }

    const updatedlatestMessages = latestMessages.map((latestMessage) => {
      if (message?.chat === latestMessage?.chat) {
        return message;
      }

      return latestMessage;
    });

    this.updateLatestMessageCreatedAt(message.chat!, (message as any).createdAt);
    this.latestMessages.next(updatedlatestMessages);
  }

  public removeLatestMessage(chat: string) {
    this.updateLatestMessageCreatedAt(chat);

    const updatedLatestMessages = this.latestMessages.getValue().filter((latestMessage) => latestMessage.chat !== chat);
    this.latestMessages.next(updatedLatestMessages);
  }

  private updateInternalChatMessageStatus(
    chatId: string,
    messageId: string,
    status: MessageStatusT,
    newMessageId?: string
  ): void {
    this.setState((state) => ({
      ...state,
      internal: state.internal.map((chat) => {
        if (chat._id === chatId && chat.messages) {
          return {
            ...chat,
            messages: chat.messages.map((message) => {
              if (message._id === messageId) {
                return { ...message, _id: newMessageId, status };
              }
              return message;
            }),
          };
        }
        return chat;
      }),
    }));
  }

  private updateChatMessageStatus(
    chatId: string,
    messageId: string,
    status: MessageStatusT,
    newMessageId?: string,
    errors?: MessageErrorI[]
  ): void {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId && chat.messages) {
          return {
            ...chat,
            messages: chat.messages.map((message) => {
              if (message._id === messageId || message._id === newMessageId) {
                return { ...message, status, _id: newMessageId, errors };
              }
              return message;
            }),
          };
        }
        return chat;
      }),
    }));
  }

  public addStepInChat(chatId: string, step) {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          delete step?.chat;
          return { ...chat, steps: [...(chat.steps || []), step] };
        }
        return chat;
      }),
      internal: state.internal.map((chatInternal) => {
        if (chatInternal._id === chatId) {
          delete step?.chat;
          return { ...chatInternal, steps: [...(chatInternal.steps || []), step] };
        }
        return chatInternal;
      }),
    }));
  }

  private addMessageInChat(chatId: string, message: MessageI): void {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          if (chat._id === state.currentChatId && !document.hidden) {
            if (this.messageHelper.getSenderTypeByMessage(message) === SenderTypeE.CONTACT) {
              message.status = 'read';
            }
            this.setAllContactChatMessagesAsRead(chat._id!);
          }
          const messages = (chat.messages || []).filter((messageItem) => messageItem._id !== message._id);
          return { ...chat, messages: [...messages, message] };
        }
        return chat;
      }),
    }));
  }
  public async setAllContactChatMessagesAsRead(chatId) {
    const result = await firstValueFrom(this.chatService.setAllMessagesAsRead(chatId, SenderTypeE.CONTACT));
    if (result.modifiedCount) {
      this.updateMessageStatusAsReadByChatId(chatId, SenderTypeE.CONTACT);
    }
  }
  private addMessageInInternalChat(chatId: string, message: MessageI): void {
    this.setState((state) => ({
      ...state,
      internal: state.internal.map((chat) => {
        if (chat._id === chatId) {
          if (this.messageHelper.getSenderTypeByMessage(message) === SenderTypeE.AGENT) {
            message.status = 'read';
          }

          if (chatId === state.currentChatId && message.sender._id !== this.getUserId()) {
            this.chatService.setAllMessagesAsRead(chat._id, SenderTypeE.AGENT).subscribe((result) => {
              if (result.modifiedCount) {
                this.updateMessageStatusAsReadByChatId(chat._id!, SenderTypeE.AGENT, false, ChatStatusE.INTERNAL, true);
              }
            });
          }

          return { ...chat, messages: [...(chat.messages || []), message] };
        }

        return chat;
      }),
    }));
  }

  public async reloadFinishedChats(page?: number) {
    const user = this.userHelper.getSocketLoginData()!;
    const { _id, team } = user;

    if (!page) {
      this.clearChatByType(ChatStatusE.FINISHED);
    }
    const finishedChats = await firstValueFrom(
      this.chatService.getAllFinishedChatsOnlyLast({ _id, team }, page, 10, '', {}, { finishedAt: -1 }).pipe(
        switchMap((chats: PageI<ChatI>) => {
          return from(Promise.all(chats.data.map((chat) => chat)));
        })
      )
    );
    this.populateStore(finishedChats);
  }

  public filterChatsByChannel(channelType: ChannelT): Observable<ChatI[]> {
    if (channelType === 'all') return this.chats$;
    return this.chats$.pipe(map((chats) => chats.filter((chat) => chat.channel.type === channelType)));
  }

  private filterQueueByChannel(channelType: ChannelT): Observable<ChatI[]> {
    if (channelType === 'all') return this.queue$;
    return this.queue$.pipe(map((queue) => queue.filter((chat) => chat.channel.type === channelType)));
  }

  private filterQueueByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.queue$;
    return this.queue$.pipe(
      map((queue) =>
        queue.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterChatsByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.chats$;
    return this.chats$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterInternalByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.internal$;
    return this.internal$.pipe(
      map((internal) =>
        internal.filter((chat) =>
          chat.internalAgents.find((internal: any) => {
            return (
              internal?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
              internal?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
              internal?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
              internal?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
            );
          })
        )
      )
    );
  }

  private filterFinishedByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.finished$;
    return this.finished$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterArchivedByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.archived$;
    return this.archived$.pipe(
      map((chats) =>
        chats.filter(
          (chat) =>
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.internalAgents.find((internal: any) => {
              return (
                internal?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
                internal?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
                internal?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
                internal?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
              );
            })
        )
      )
    );
  }

  private filterFlowByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.flow$;
    return this.flow$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterWorkflowByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.workflow$;
    return this.workflow$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterAIAssistedByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.aiAssisted$;
    return this.aiAssisted$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterInQueueByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.inQueue$;
    return this.inQueue$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterNewByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.new$;
    return this.new$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterTransferByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.transfer$;
    return this.transfer$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterDelayedByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.delayed$;
    return this.delayed$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterWaitingByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.waiting$;
    return this.waiting$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterApiByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.api$;
    return this.api$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private filterKanbanByContactInfo(contactInfo: string, regex: RegExp): Observable<ChatI[]> {
    if (!contactInfo) return this.kanban$;
    return this.kanban$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          return (
            chat.contact?.telephone?.toLowerCase()?.includes(this.normalizeTelephone(contactInfo.toLowerCase())) ||
            chat.contact?._id?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.name?.toLowerCase()?.includes(contactInfo.toLowerCase()) ||
            chat.contact?.email?.toLowerCase()?.includes(contactInfo.toLowerCase())
          );
        })
      )
    );
  }

  private normalizeTelephone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  public clearKanbanChats() {
    this.setState((state) => ({ ...state, kanban: [] }));
  }

  private countInternalNotifications(chat: ChatI, messages: MessageI[] | [] | undefined): number {
    let count = 0;

    if (!messages) {
      return count;
    }

    for (const message of cloneArray(messages).reverse()) {
      if (
        !this.userHelper.verifyIfUserIsCurrentUser(message?.sender?.senderId) &&
        (this.notifyMessageInternalChat(chat, message) || this.notifyMention(message))
      ) {
        if (['sent', 'delivered'].includes(message.status)) {
          count++;
        }
      }
    }

    return count;
  }

  private notifyMessageInternalChat(chat: ChatI, message: MessageI): boolean {
    return (
      message.type !== MessageTypeE.NOTE && !!chat.internalAgents.find((internal) => internal._id === this.getUserId())
    );
  }

  private notifyMention(message: MessageI): boolean {
    return (
      message.type === MessageTypeE.NOTE &&
      !!message.content.mentions?.find((mention) => mention.choice._id === this.getUserId())
    );
  }

  updateChat(currentChat?: ChatI) {
    if (!currentChat) return;
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === currentChat._id) {
          return currentChat;
        }
        return chat;
      }),
      internal: state.internal.map((chat) => {
        if (chat._id === currentChat._id) {
          return currentChat;
        }
        return chat;
      }),
    }));
  }

  public setChatTypes(chatId, types: MessageTypeT[]) {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            types,
          };
        }
        return chat;
      }),
      internal: state.internal.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            types,
          };
        }
        return chat;
      }),
    }));
  }

  setContactAttendance(contact: ContactI) {
    this.setState((state) => ({
      ...state,
      contactAttendance: contact,
    }));
  }

  async addNotification(id: string, status: ChatStatusE, inQueue?: boolean) {
    const chat = this.existsChatInStore({ _id: id });
    if (!chat) return;

    this.setState((state) => {
      const stateNotifications = state.notificationChats;
      this.removeNotificationFromList(stateNotifications, id);
      this.addNotificationInList({ _id: id, count: 1, status, inQueue }, stateNotifications);

      return {
        ...state,
        notificationChats: {
          ...stateNotifications,
          totalBySelectedChatType: this.countNotificationsByChatType(stateNotifications, [state.chatType]),
          totals: this.countNotificationsForAttendance(stateNotifications),
        },
      };
    });
  }

  private findNotificationById(chatId: string): chatNotificationI | undefined {
    const stateNotifications = this.get().notificationChats;
    const mapStoreChats = this.chatHelper.mapStoreChats();

    for (const mappedStoreChat of mapStoreChats) {
      const { property } = mappedStoreChat;
      const listChatsNotification = stateNotifications[property] || [];
      const chat = listChatsNotification.find((chat) => chat._id === chatId);

      if (chat) {
        return chat;
      }
    }

    return;
  }

  public setNotification(newNotifications: chatNotificationI[]): void {
    this.setState((state) => {
      const stateNotifications = state.notificationChats;

      for (const chatNotification of newNotifications) {
        this.removeNotificationFromList(stateNotifications, chatNotification._id);
        this.addNotificationInList(chatNotification, stateNotifications);
      }

      return {
        ...state,
        notificationChats: {
          ...stateNotifications,
          totalBySelectedChatType: this.countNotificationsByChatType(stateNotifications, [state.chatType]),
          totals: this.countNotificationsForAttendance(stateNotifications),
        },
      };
    });
  }

  private addNotificationInList(chatNotification: chatNotificationI, stateNotifications: NotificationChatsI) {
    const mapStoreChats = this.chatHelper.mapStoreChats().filter((map) => map.status === chatNotification.status);

    for (const mappedStoreChat of mapStoreChats) {
      const { property } = mappedStoreChat;

      if (chatNotification.status === ChatStatusE.QUEUE) {
        const isInQueue = chatNotification.inQueue;
        if ((property === PropertyChatE.IN_QUEUE && !isInQueue) || (property === PropertyChatE.QUEUE && isInQueue)) {
          continue;
        }
      }

      const listChatsNotification = stateNotifications[property] || [];
      const exists = !!listChatsNotification.find((chat) => chat._id === chatNotification._id);

      if (!exists) {
        listChatsNotification.push(chatNotification);
      }

      stateNotifications[property] = listChatsNotification;
    }
  }

  private countNotificationsForAttendance(notifications: NotificationChatsI): number {
    return this.countNotificationsByChatType(notifications, ['attendances', 'waiting', 'internal']);
  }

  private countNotificationsByChatType(notifications: NotificationChatsI, chatType: ChatT[]): number {
    const propertys = this.chatHelper
      .mapStoreChats()
      .filter((map) => chatType.includes(map.chatType))
      .map((map) => map.property);
    const uniquePropertys = [...new Set(propertys)];

    const count = uniquePropertys.reduce((acc, property) => {
      acc = (acc || 0) + (notifications[property]?.length || 0);
      return acc;
    }, 0 as number);
    return count;
  }

  private removeNotification(stateStore: ChatsState, chatId: string): NotificationChatsI {
    const notification = stateStore.notificationChats;
    this.removeNotificationFromList(notification, chatId);

    return {
      ...notification,
      totals: this.countNotificationsForAttendance(notification),
      totalBySelectedChatType: this.countNotificationsByChatType(notification, [stateStore.chatType]),
    };
  }

  private removeNotificationFromList(notificationChats: NotificationChatsI, chatId: string): NotificationChatsI {
    const mapStoreChats = this.chatHelper.mapStoreChats();

    for (const mappedStoreChat of mapStoreChats) {
      const { property } = mappedStoreChat;
      const listChatsNotification = notificationChats[property] || [];
      const filteredList = listChatsNotification.filter((chat) => chat._id !== chatId);
      notificationChats[property] = filteredList;
    }
    return notificationChats;
  }

  addNotificationMention(id: string, status: ChatStatusT) {
    this.setState((state) => ({
      ...state,
      notificationChatsMention: {
        chats: [...state.notificationChatsMention.chats, { _id: id, count: 1, status }],
        totals: state.notificationChatsMention.totals + 1,
      },
    }));
  }

  public updateTotalNotificationBySelectedType() {
    this.setState((state) => {
      const notification = state.notificationChats;

      return {
        ...state,
        notificationChats: {
          ...notification,
          totalBySelectedChatType: this.countNotificationsByChatType(notification, [state.chatType]),
        },
      };
    });
  }

  removeNotificationMention(notification: NotificationChatsMentionI, chatId: string) {
    const filteredChats = notification.chats.filter((chat) => !(chat._id === chatId && chat.status !== 'queue'));
    return {
      chats: filteredChats,
      totals: filteredChats.length,
    };
  }

  setForwardMessage(message: MessageI | null) {
    const chatId = this.get().currentChatId;
    if (!chatId) {
      return;
    }
    this.setState((state) => {
      const forwardingMessages = { ...state.forwardingMessages };
      if (message) {
        forwardingMessages[chatId] = message;
      } else {
        delete forwardingMessages[chatId];
      }
      return { ...state, forwardingMessages };
    });
  }

  public addFilesMessage(file: ChatStateFileMessageI[]) {
    this.setState((state) => ({
      ...state,
      filesMessage: [...(state.filesMessage || []), ...file],
    }));
  }

  public setFilesMessage(files: ChatStateFileMessageI[]) {
    this.patchState({ filesMessage: files });
  }

  public updateFileMessage(fileId: string, file: ChatStateFileMessageI) {
    this.setState((state) => ({
      ...state,
      filesMessage:
        state.filesMessage?.map((value) => (value.id && fileId && value.id === fileId ? file : value)) ?? [],
    }));
  }

  public removeFilesMessageById(fileId: string) {
    this.setState((state) => ({
      ...state,
      filesMessage: state.filesMessage?.filter((value) => value.id && fileId && value.id !== fileId) ?? [],
    }));
  }

  public populateChatsFilter(type: ChatT, chats: ChatI[], clean: boolean = false): void {
    this.setState((state) => {
      const newChatsFilter = { ...state.chatsFilter };
      newChatsFilter[type] = clean ? [] : [...(newChatsFilter[type] || []), ...chats];
      return { ...state, chatsFilter: newChatsFilter };
    });
  }

  public removeChatsFilter(type: ChatT): void {
    this.setState((state) => {
      const newChatsFilter = { ...state.chatsFilter, [type]: [] };
      return { ...state, chatsFilter: newChatsFilter };
    });
  }

  public updateLatestMessageCreatedAt(chatId: string, createdAt?: string) {
    const chatTypes = [
      ChatStatusE.QUEUE,
      'chats',
      ChatStatusE.INTERNAL,
      'flow',
      ChatStatusE.WORKFLOW,
      ChatStatusE.API,
      ChatStatusE.DELAYED,
      ChatStatusE.WAITING,
    ];
    this.setState((state) => {
      const newState = { ...state };
      for (const chatType of chatTypes) {
        if (Array.isArray(newState[chatType])) {
          newState[chatType] = newState[chatType].map((chat) => {
            let latestMessageCreatedAt = createdAt;
            if (chat._id === chatId) {
              if (chat?.latestMessageCreatedAt && createdAt) {
                const oldDate = new Date(chat?.latestMessageCreatedAt);
                const newDate = new Date(createdAt);
                latestMessageCreatedAt = oldDate > newDate ? chat?.latestMessageCreatedAt : latestMessageCreatedAt;
              }
              return { ...chat, latestMessageCreatedAt };
            }
            return chat;
          });
        }
      }
      return newState;
    });
  }

  public setSelectedChatId(chatId: string = '') {
    this.selectedChatId = chatId;
  }

  public async existsChatInStore(chatFind) {
    const chatExist = await firstValueFrom(
      this.select((state) => {
        const allChats = [
          ...state.chats,
          ...state.queue,
          ...state.in_queue,
          ...state.internal,
          ...state.finished,
          ...state.archived,
          ...state.flow,
          ...state.workflow,
          ...state.ai_assisted,
          ...state.new,
          ...state.api,
          ...state.delayed,
          ...state.waiting,
        ];
        return allChats.find((chat) => chat._id === chatFind._id);
      })
    );
    return chatExist;
  }

  private brazilianNumberHelper(phoneNumber?: string) {
    if (!phoneNumber) return '';
    const ddi = phoneNumber.slice(0, 2);
    const ddd = phoneNumber.slice(2, 4);
    let numberWithoutDdiAndDdd = phoneNumber.slice(4);
    if (numberWithoutDdiAndDdd.length === 8) {
      numberWithoutDdiAndDdd = `9${numberWithoutDdiAndDdd}`;
    }
    const phoneNumberWithNine = `${ddi}${ddd}${numberWithoutDdiAndDdd}`;
    return phoneNumberWithNine;
  }

  public getChatByContactIdPhoneNumber(
    contactId: string,
    phoneNumber: string,
    channelId: string
  ): Observable<ChatI | undefined> {
    return this.select((state) => {
      const chat = state.chats.find(
        (chat) =>
          chat.channel?._id === channelId &&
          chat.contact?._id === contactId &&
          (!chat.phoneNumber ||
            chat.phoneNumber === phoneNumber ||
            this.brazilianNumberHelper(chat.phoneNumber) === phoneNumber ||
            chat.phoneNumber === this.brazilianNumberHelper(phoneNumber))
      );
      if (chat) return chat;
      return undefined;
    });
  }

  public updateLoyaltyAllQueues(loyalty: LoyaltyI, remove: boolean = false) {
    this.getAllQueues().forEach(({ stream, property, status }) => {
      this.setState((state) => {
        const queue = state[property];

        queue.map((chatQueue: ChatI) => {
          const update =
            chatQueue.contact?.customerId === loyalty.customer && chatQueue.contact?._id === loyalty.contact;

          if (update) {
            if (remove) {
              chatQueue.contact?.loyalties?.map((loyaltyQueue, index) => {
                if (loyaltyQueue._id === loyalty._id) {
                  chatQueue.contact?.loyalties!.splice(index, 1);
                }
              });
            } else {
              chatQueue.contact?.loyalties?.push(loyalty);
            }
          }
        });

        return { ...state, [property]: [...queue] };
      });
    });
  }

  public clearAll() {
    this.setState((state) => ChatStoreService.initialState(state.chatType));
    this.otherTeams = [];
  }

  public clearAllQueues() {
    this.setState((state) => ({
      ...state,
      queue: [],
      chats: [],
      internal: [],
      finished: [],
      archived: [],
      flow: [],
      workflow: [],
      ai_assisted: [],
      api: [],
      delayed: [],
      waiting: [],
      in_queue: [],
    }));
  }

  public clearNotifications() {
    this.setState((state) => ({
      ...state,
      notificationChats: {
        queue: [],
        chats: [],
        internal: [],
        finished: [],
        archived: [],
        flow: [],
        workflow: [],
        ai_assisted: [],
        api: [],
        delayed: [],
        waiting: [],
        in_queue: [],
        kanban: [],
        new: [],
        transfer: [],
        totals: 0,
        totalBySelectedChatType: 0,
      },
      notificationChatsMention: { chats: [], totals: 0 },
    }));
  }

  public async updateContactInAllChats(contact: any) {
    this.setState((state) => {
      const chatTypes = [
        'chats',
        'queue',
        'internal',
        'archived',
        'finished',
        'flow',
        'workflow',
        'ai_assisted',
        'new',
        'api',
        'delayed',
        ChatStatusE.WAITING,
      ];
      const updatedChatsByType: { [key: string]: any[] } = {};
      const updatedChatsToEmit: any[] = [];

      for (const chatType of chatTypes) {
        const chats = state[chatType];
        if (chats) {
          updatedChatsByType[chatType] = chats.map((chat) => {
            if (chat.contact?._id === contact._id && contact._id) {
              const updatedChat = { ...chat, contact: { ...chat.contact, ...contact } };
              updatedChatsToEmit.push(updatedChat);
              return updatedChat;
            }
            return chat;
          });
        }
      }

      updatedChatsToEmit.forEach((chat) => this.updateChatContactSubject.next(chat));

      const newState = {
        ...state,
        ...updatedChatsByType,
      };

      if (newState.contactAttendance) {
        if (newState.contactAttendance._id === contact._id && contact._id) {
          newState.contactAttendance = {
            ...newState.contactAttendance,
            ...contact,
          };
        }
      }
      return newState;
    });
  }

  public async replaceFlowChatContact(chatId: string, newContact: ContactI) {
    this.setState((state) => {
      const flowChats = state.flow;

      const newFlowChats =
        flowChats?.map((chat) => {
          if (chat._id === chatId && newContact._id) {
            chat.contact = newContact;
            this.updateChatContactSubject.next(chat);
          }
          return chat;
        }) ?? [];

      const newState = {
        ...state,
        flow: newFlowChats,
      };

      if (newState.contactAttendance?._id === newContact._id) {
        newState.contactAttendance = { ...newState.contactAttendance, ...newContact };
      }

      return newState;
    });
  }

  public updateMessageInChat(chatId: string, message: MessageI): void {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            messages: (chat.messages ?? []).map((chatMessage) =>
              chatMessage._id === message._id ? message : chatMessage
            ),
          };
        }
        return chat;
      }),
      internal: state.internal.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            messages: (chat.messages ?? []).map((chatMessage) =>
              chatMessage._id === message._id ? message : chatMessage
            ),
          };
        }
        return chat;
      }),
    }));
  }

  changeContact(chatUpdated: ChatI) {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatUpdated._id) {
          return {
            ...chat,
            contact: chatUpdated.contact,
          };
        }
        return chat;
      }),
      internal: state.internal.map((chat) => {
        if (chat._id === chatUpdated._id) {
          return {
            ...chat,
            contact: chatUpdated.contact,
          };
        }
        return chat;
      }),
    }));
    // set in all dependencies the chat updated
    this.updateChatContactSubject.next(chatUpdated);
    this.setState((state) => {
      if (state.currentChatId === chatUpdated?._id) {
        this.setCurrentChatId('');
        this.setCurrentChatId(state.currentChatId);
      }
      return {
        ...state,
      };
    });
  }

  public addMentionOnChat(message: MessageI | null) {
    if (!message) {
      return;
    }

    if (message?.sender?._id === this.getUserId()) {
      return;
    }

    const usersMentioned: Partial<UserI>[] = ((message as MessageI)?.content?.mentions ?? [])
      .filter((message) => !!message.choice)
      .map((message) => message.choice);

    if (!usersMentioned?.length) {
      return;
    }
    const chatId = message?.chat;
    if (usersMentioned?.length && chatId) {
      this.setState((state) => {
        const newState = state;
        for (const key of ['internal', 'chats']) {
          const list = state[key];
          if (list?.length) {
            state[key] = list.map((chat) => {
              if (chat._id === chatId) {
                const mentions = chat?.mentions ?? [];
                for (const userMentioned of usersMentioned) {
                  const found = mentions.find((row) => row.agent?._id === userMentioned._id);
                  if (!found) {
                    mentions.push({
                      agent: {
                        _id: userMentioned._id!,
                        name: userMentioned.name!,
                      } as unknown as UserI,
                      count: 1,
                      status: 'unread',
                    });
                  } else {
                    found.count += 1;
                  }
                }
                return {
                  ...chat,
                  mentions,
                };
              }
              return chat;
            });
          }
        }
        return newState;
      });
    }
  }
  updateMentionsInChat(chatId: string, chatMentions: ChatMentionI[] | undefined) {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            mentions: chatMentions,
          };
        }
        return chat;
      }),
      internal: state.internal.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            mentions: chatMentions,
          };
        }
        return chat;
      }),
    }));
  }

  public async getMentionsInChat(chatId: string): Promise<ChatMentionI[]> {
    const chat = await firstValueFrom(this.getChatById(chatId));

    if (chat && chat.mentions) {
      return chat.mentions;
    }

    return [];
  }

  public newInternalChat(chat: ChatI) {
    this.newInternalChatSubject.next(chat);
  }

  public getNotification(type: ChatStatusT, chatId: string) {
    if (type === ChatStatusE.INTERNAL) {
      return this.getNotificationTotalsInternal(chatId);
    }

    return this.select((state) => {
      let count = 0;

      const chats = state[type];

      if (chats && chats.length > 0) {
        chats.forEach((chat: ChatI) => {
          if (chat._id === chatId) {
            count += chat?.unreadMessagesCount ?? 0;
          }
        });
      }

      return count;
    });
  }

  private getNotificationTotalsInternal(chatId?: string) {
    return this.select((state) => {
      let count = 0;

      const chats = state[ChatStatusE.INTERNAL];

      if (chats && chats.length > 0) {
        chats.forEach((chat: ChatI) => {
          if (!chatId || chat._id === chatId) {
            count += this.countInternalNotifications(chat, chat.messages);
          }
        });
      }

      return count;
    });
  }

  public setAllInternalChatMessagesAsRead(chatId: string) {
    this.chatService.setAllMessagesAsRead(chatId, SenderTypeE.AGENT).subscribe((result) => {
      if (result.modifiedCount) {
        this.updateMessageStatusAsReadByChatId(chatId, SenderTypeE.AGENT, false, ChatStatusE.INTERNAL, false);
      }
    });
  }

  public sortChat(a, b, sortChat) {
    const latestStepACreatedAt = a.steps?.at(-1)?.createdAt;
    const latestStepBCreatedAt = b.steps?.at(-1)?.createdAt;

    const createdAtA = a?.latestMessageCreatedAt ?? a?.createdAt;
    const createdAtB = b?.latestMessageCreatedAt ?? b?.createdAt;

    if (!createdAtA) {
      return -1;
    }

    if (!createdAtB) {
      return 1;
    }

    if (!createdAtA && !createdAtB) {
      return 0;
    }

    let aTime = +new Date(createdAtA).getTime().toString();
    let bTime = +new Date(createdAtB).getTime().toString();

    if (latestStepACreatedAt) {
      const latestStepATime = +new Date(latestStepACreatedAt).getTime();
      aTime = Math.max(aTime, latestStepATime);
    }

    if (latestStepBCreatedAt) {
      const latestStepBTime = +new Date(latestStepBCreatedAt).getTime();
      bTime = Math.max(bTime, latestStepBTime);
    }

    const result = sortChat < 0 ? bTime - aTime : aTime - bTime;

    return Number.isNaN(result) ? 0 : result;
  }

  public sortChatFinished(a, b, sortChat) {
    if (a.finishedAt && b.finishedAt) {
      let aTime = new Date(a.finishedAt).getTime().toString();
      let bTime = new Date(b.finishedAt).getTime().toString();

      if (moment(aTime).isValid()) {
        aTime = moment(aTime).toDate().getTime().toString();
      }

      if (moment(bTime).isValid()) {
        bTime = moment(bTime).toDate().getTime().toString();
      }

      const result = sortChat < 0 ? +bTime - +aTime : +aTime - +bTime;
      return Number.isNaN(result) ? 0 : result;
    }
    return 0;
  }

  public filterInternalChats(searchText: string, onlyMentions: boolean = false): Observable<ChatI[]> {
    return this.internal$.pipe(
      map((chats) =>
        chats.filter((chat) => {
          const hasSearchText = this.hasInternalSearchText(chat, searchText);
          const hasOnlyMentions = this.hasInternalOnlyMentions(chat, onlyMentions);

          return hasSearchText && hasOnlyMentions;
        })
      )
    );
  }

  private hasInternalSearchText(chat: ChatI, searchText: string) {
    if (!searchText) return true;

    const userId = this.userHelper.getUserAttributes()['custom:userId'];

    return chat.internalAgents
      .filter((internal) => internal._id !== userId)
      .some((internal: any) => {
        if (!internal) return false;

        return (
          internal?.telephone?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          internal?.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          internal?.email?.toLowerCase()?.includes(searchText.toLowerCase())
        );
      });
  }

  private hasInternalOnlyMentions(chat: ChatI, onlyMentions: boolean = false) {
    if (!onlyMentions) return true;

    if (chat.mentions && chat.mentions.length > 0) {
      const userId = this.userHelper.getUserAttributes()['custom:userId'];

      return chat.mentions.find((mention) => mention.agent?._id === userId);
    }

    return false;
  }

  private getUserId(): string {
    return this.userHelper.getUserAttributes()['custom:userId'];
  }

  public updateChatKanbanCategory(chatId: string, kanbanCategoryId: string): void {
    this.setState((state) => ({
      ...state,
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            kanbanCategory: kanbanCategoryId,
          };
        }
        return chat;
      }),
      internal: state.internal.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            kanbanCategory: kanbanCategoryId,
          };
        }
        return chat;
      }),
    }));
  }
}
